import { useTranslation } from 'react-i18next';

import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { Input, Modal, SingleSelect } from '@/common';
import { Button, Spinner, Textarea } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { getRelationshipOptions, getSpeechDurationOptions } from '@/helpers';

type GenerateTextFormValues = {
  content: string;
  relationship?: string;
  keyTheme?: string;
  desiredTone?: string;
  speechDuration?: string;
};

type GenerateTextModalProps = {
  eventId: number;
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (values: GenerateTextFormValues & { eventId: number }) => void;
  isGenerating?: boolean;
};

const generateTextSchema = Yup.object().shape({
  content: Yup.string().required('Content is required')
});

export const GenerateSpeechTextModal = ({
  isOpen,
  onClose,
  onGenerate,
  eventId,
  isGenerating = false
}: GenerateTextModalProps) => {
  const [t] = useTranslation('common');
  const SPEECH_DURATION_OPTIONS = getSpeechDurationOptions();
  const RELATIONSHIP_OPTIONS = getRelationshipOptions();

  const initialValues: GenerateTextFormValues = {
    content: '',
    relationship: '',
    keyTheme: '',
    desiredTone: '',
    speechDuration: ''
  };

  const handleSubmit = (values: GenerateTextFormValues) => {
    onGenerate({ ...values, eventId });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalClasses='w-full max-w-2xl'
      height='auto'
      hasCloseIcon={true}
      label={t('generateSpeech')}
      labelClasses='ml-2 text-lg font-bold text-primary-blue'>
      <div className='flex flex-col gap-6'>
        <Formik
          key={Number(isOpen)}
          initialValues={initialValues}
          validationSchema={generateTextSchema}
          onSubmit={handleSubmit}>
          {({ errors, touched, values, setFieldValue }: FormikProps<GenerateTextFormValues>) => (
            <Form className='flex flex-col gap-6 px-2'>
              <div className='border-y border-secondary-blue-gray py-3'>
                <div className='flex flex-col gap-3 py-2'>
                  <Textarea
                    id='content'
                    label={t('content')}
                    isRequired
                    errorMessage={errors.content}
                    isInvalid={!!(errors.content && touched.content)}
                    className='h-24'
                    readOnly={isGenerating}
                    placeholder={t('contentPlaceholder')}
                  />
                  <div className='flex gap-4'>
                    <div className='w-1/2'>
                      <SingleSelect
                        id='relationship'
                        label={t('relationship')}
                        value={
                          RELATIONSHIP_OPTIONS.find(
                            (option) => option.value === values.relationship
                          ) || null
                        }
                        options={RELATIONSHIP_OPTIONS}
                        placeholder={t('selectRelationship')}
                        disabled={isGenerating}
                        handleChange={(selectedOption) =>
                          setFieldValue('relationship', selectedOption?.value || '')
                        }
                      />
                    </div>

                    <div className='flex-1'>
                      <SingleSelect
                        id='speechDuration'
                        label={t('speechDuration')}
                        value={
                          SPEECH_DURATION_OPTIONS.find(
                            (option) => option.value === values.speechDuration
                          ) || null
                        }
                        options={SPEECH_DURATION_OPTIONS}
                        placeholder={t('selectSpeechDuration')}
                        disabled={isGenerating}
                        handleChange={(selectedOption) =>
                          setFieldValue('speechDuration', selectedOption?.value || '')
                        }
                      />
                    </div>
                  </div>
                  <Input
                    id='desiredTone'
                    label={t('desiredTone')}
                    type='text'
                    readOnly={isGenerating}
                    placeholder={t('desiredTonePlaceholder')}
                  />
                  <Input
                    id='keyTheme'
                    label={t('keyTheme')}
                    type='text'
                    readOnly={isGenerating}
                    placeholder={t('keyThemePlaceholder')}
                  />
                </div>
              </div>

              <div className='flex justify-end gap-3'>
                <Button
                  variant={ButtonVariant.SECONDARY}
                  type='button'
                  handleOnClick={onClose}
                  disabled={isGenerating}>
                  {t('cancel')}
                </Button>
                <Button type='submit' disabled={isGenerating}>
                  <div className='flex items-center justify-center gap-2'>
                    {isGenerating && <Spinner fill='white' className='size-5' />}
                    {isGenerating ? t('generatingSpeechText') : t('generate')}
                  </div>
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};
